// Pure, dependency-free merge rule for a field's `historical_data`.
// Shared by web and mobile — keep platform-agnostic.
//
// WHY
//
// Two API endpoints write the same `historical_data.wfr` / `wfr_length` slot
// and are fetched concurrently on every field load, so whichever lands last
// wins (see irrigationAmount.js for the rounding side of the same race):
//
//   get_field_history          FieldManager — rich: pressure, pressure_unit,
//                              pressure_alert on each irrigation event
//   get_farming_units_history  SensorManager.structure_wfr_for_farming_unit —
//                              lean: value, endDate, manufacturer only
//
// A lean farming-unit response landing last used to wipe every
// `pressure_alert`, so the graph markers and the tooltip line vanished.
//
// RULE
//
// Only the farming-unit merge uses this. For each event the lean response
// carries without pressure, keep the pressure keys the existing event already
// has; everything else (amount included) comes from the incoming event. The
// field-history merge stays a plain replace, so a newer field history can
// still clear an alert that no longer applies.

const PRESSURE_SERIES = ['wfr', 'wfr_length'];
const PRESSURE_KEYS = ['pressure', 'pressure_unit', 'pressure_alert'];

const isObject = value => value !== null && typeof value === 'object';

// Pressure keys of `event`, or null when it has none.
const pressureOf = event => {
  if (!isObject(event)) {
    return null;
  }
  let picked = null;
  PRESSURE_KEYS.forEach(key => {
    if (event[key] !== undefined && event[key] !== null) {
      picked = picked || {};
      picked[key] = event[key];
    }
  });
  return picked;
};

// `incoming` (one field's farming-unit history) with the pressure keys of
// `existing` (that field's current historical_data) carried onto events that
// lack them. Returns `incoming` itself when nothing needs carrying over.
const keepFieldPressure = (existing, incoming) => {
  if (!isObject(existing) || !isObject(incoming)) {
    return incoming;
  }
  let merged = incoming;
  PRESSURE_SERIES.forEach(seriesKey => {
    const existingValues = existing[seriesKey]?.values;
    const incomingValues = incoming[seriesKey]?.values;
    if (!isObject(existingValues) || !isObject(incomingValues)) {
      return;
    }
    let values = null;
    Object.keys(incomingValues).forEach(date => {
      const event = incomingValues[date];
      if (!isObject(event) || pressureOf(event)) {
        return;
      }
      const kept = pressureOf(existingValues[date]);
      if (kept) {
        values = values || { ...incomingValues };
        values[date] = { ...event, ...kept };
      }
    });
    if (values) {
      merged = {
        ...merged,
        [seriesKey]: { ...incoming[seriesKey], values },
      };
    }
  });
  return merged;
};

module.exports = {
  keepFieldPressure,
};
